import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Enrollment,
  InvestmentPathCard,
  InvestmentPathType,
  LinkButton,
  Preloader,
  Text,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { INVESTMENTS_ERROR_KEY } from '@/common/constants';
import AppButton from '@/components/controls/AppButton';
import { ErrorBoundary } from '@/modules/core/components';
import { useAllowedCountriesQuery } from '@/modules/core/queries';
import theme from '@/styles/theme';

import useTermsAndConditionsDocsQuery, { DocsEntity } from '../../docs/useTermsAndConditionsDocs.query';
import { InvestmentPaths } from '../../investments.types';
import ServerErrorModal from '../../modal/ServerErrorModal';
import { useInvestmentPathQuery } from '../../queries';
import PersonalInfoModal from '../PersonalInfoModal';
import { useEmployeeData } from '../PersonalInfoModal/PersonalInfoForm/hooks';

import { useInvestmentPathStore } from './stores/useInvestmentPath.store';
import InfoModal from './InfoModal';

interface Props {
  onNext: (key?: string) => void;
  onCancel: () => void;
  stepLength: number;
  step: number;
  currentMyAccount: Enrollment;
  isLoading?: boolean;
}

const STEPS_COUNT: {
  [key: string]: number;
} = {
  MANAGED: 4,
  SELF_DIRECTED: 4,
  BROKERAGE: 4,
};
const BUTTON_WIDTH = '130px';

const InvestmentPath: React.FC<Props> = ({
  onNext,
  onCancel,
  stepLength,
  step,
  currentMyAccount,
  isLoading,
}) => {
  const BLOCKED_TYPES = useMemo(() => {
    return [InvestmentPathType.BROKERAGE];
  }, []);
  const [citizenshipRejected, setCitizenshipRejected] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isPersonalInfoSuccessModalVisible, setIsPersonalInfoSuccessModalVisible] = useState(false);
  const { t } = useTranslation();
  const { INVESTMENT_PATH_ERROR_TEXT, AGREEMENT_ERROR_TEXT } = INVESTMENTS_ERROR_KEY;
  const { values, handleSetValues } = useInvestmentPathStore();

  const { citizenshipCountryAlpha2 } = useEmployeeData();
  const { allowedCountriesMap } = useAllowedCountriesQuery();

  const isAllowedCitizenship = useMemo(() => {
    if (citizenshipCountryAlpha2) {
      return allowedCountriesMap?.[citizenshipCountryAlpha2] || false;
    }
    return null;
  }, [allowedCountriesMap, citizenshipCountryAlpha2]);

  const [selectedPath, setInvestmentPath] = useState('');

  const [isSubmitted, setSubmitted] = useState(false);

  const { handleSave: handleSavePath, isLoading: isPathSaveLoading } = useInvestmentPathQuery();

  const { docsGroups, isLoading: isDocsLoading } = useTermsAndConditionsDocsQuery(selectedPath);

  const [checkedDocs, setCheckedDocs] = useState<string[]>([]);
  const handleToggleCheckedDocs = useCallback(
    (id: string) => () => {
      if (checkedDocs.includes(id)) {
        setCheckedDocs(checkedDocs.filter((item) => item !== id));
      } else {
        setCheckedDocs([...checkedDocs, id]);
      }
    },
    [checkedDocs]
  );

  const isAllDocumentsApplied = useMemo(() => {
    const docsIds = docsGroups.map((item) => item.id);
    if (_.difference(docsIds, checkedDocs).length === 0) {
      return true;
    }
    return false;
  }, [checkedDocs, docsGroups]);

  const [errorWarningVisible, setErrorWarningVisible] = useState(false);

  const currentError = useMemo(() => {
    if (!selectedPath) {
      return t(INVESTMENT_PATH_ERROR_TEXT);
    }
    if (!isAllDocumentsApplied) {
      return t(AGREEMENT_ERROR_TEXT);
    }
    return '';
  }, [selectedPath, isAllDocumentsApplied, t, INVESTMENT_PATH_ERROR_TEXT, AGREEMENT_ERROR_TEXT]);

  const handleSave = useCallback(async () => {
    await handleSavePath({
      option: selectedPath as InvestmentPaths,
    });
  }, [handleSavePath, selectedPath]);
  const handleSubmit = useCallback(async () => {
    setSubmitted(true);
    if (!!selectedPath && isAllDocumentsApplied) {
      await handleSave();
      return handleSetValues({
        investmentPath: selectedPath,
        termsCondition: isAllDocumentsApplied,
      });
    }
  }, [handleSave, handleSetValues, isAllDocumentsApplied, selectedPath]);

  const handleClick = useCallback((link: string) => {
    if (link) {
      window.open(link, '_blank');
    }
  }, []);
  const { isFull, isLoading: isProfileLoading } = useEmployeeData();

  const handleNext = useCallback(async () => {
    setSubmitted(true);
    if (currentError) {
      return;
    }
    if (isFull) {
      if (selectedPath === InvestmentPaths.SELF_DIRECTED) {
        await handleSubmit();
        onNext(selectedPath);
      } else {
        await handleSubmit();
        onNext(selectedPath);
      }
    } else {
      setVisible(true);
    }
  }, [currentError, handleSubmit, isFull, onNext, selectedPath]);

  useEffect(() => {
    if (values?.investmentPath !== '' && !selectedPath) {
      setInvestmentPath(values?.investmentPath);
    }
    // if (values?.termsCondition) {
    //   setAcceptedDocs(values?.termsCondition);
    // }
  }, [selectedPath, values]);

  useEffect(() => {
    const investmentTypes = currentMyAccount?.investmentOptionTypes;
    if (!selectedPath && investmentTypes?.includes(InvestmentPathType.MANAGED)) {
      setInvestmentPath(InvestmentPathType.MANAGED);
    }
  }, [currentMyAccount, selectedPath]);
  const currentOptions = useMemo(() => {
    return currentMyAccount?.investmentOptionTypes || [];
  }, [currentMyAccount?.investmentOptionTypes]);

  const getWrapper = useCallback((node: React.ReactNode, item: DocsEntity, list: DocsEntity[]) => {
    const delimiterSign = item.lineBreak ? <>{'. '}</> : _.last(list) == item ? <>{'. '}</> : <>{', '}</>;
    const lineBreak = item.lineBreak ? (
      <>
        <Box style={{ display: 'block' }} margin={{ bottom: 'spacing12' }} />
      </>
    ) : null;

    if (item.isModal) {
      return (
        <>
          <InfoModal key={item.title} title={item.title} text={item.text || ''}>
            <LinkButton key={item.title} size="M" linkInlineMode>
              {node}
            </LinkButton>
          </InfoModal>
          {delimiterSign}
          {lineBreak}
        </>
      );
    }
    return (
      <>
        {node}
        {delimiterSign}
        {lineBreak}
      </>
    );
  }, []);

  return (
    <Box direction="column" data-testid="Investment-path-wrapper-id">
      <Box flex="grow" pad={{ horizontal: '40px', bottom: '40px' }}>
        <Text size="3xl" color="blockTitle" weight={700} margin={{ bottom: 's' }}>
          {t('Let’s Get Started with Investing!')}
        </Text>

        <Box
          direction="row"
          justify="center"
          background="module"
          round="moduleRound"
          pad="spacing24"
          margin={{ bottom: 'spacing24' }}
          style={{ flexWrap: 'wrap' }}
        >
          {isLoading && (
            <Box flex="grow" align="center" justify="center">
              <Preloader />
            </Box>
          )}
          {currentOptions.map((option) => (
            <InvestmentPathCard
              key={option}
              type={option}
              testId={option}
              commingSoon={BLOCKED_TYPES.includes(option)}
              checked={selectedPath === option}
              onClick={() => setInvestmentPath(option)}
            />
          ))}
        </Box>
        {((!isFull && !isProfileLoading) ||
          isPersonalInfoSuccessModalVisible ||
          isAllowedCitizenship === false) && (
          <PersonalInfoModal
            visible={visible}
            isShowChangeCitizenshipDisplayed={isAllowedCitizenship === false}
            onSetVisible={setVisible}
            onResetRejectByCitizenship={() => setCitizenshipRejected(false)}
            onRejectByCitizenship={() => setCitizenshipRejected(true)}
            onSetVisibleSuccessModal={setIsPersonalInfoSuccessModalVisible}
          />
        )}

        {currentError && isSubmitted && (
          <Box align="end" width="100%" margin={{ bottom: 'spacing24' }}>
            <Text size="medium" color="danger" style={{ lineHeight: '20px' }}>
              {t(currentError)}
            </Text>
          </Box>
        )}
        <Box direction="row" align="start">
          <ErrorBoundary>
            <Box fill margin={{ right: 'spacing72' }}>
              {isDocsLoading && <Preloader size={12} />}
              {docsGroups?.map((group, index) => (
                <Box margin={{ top: 's' }} direction="row" key={index}>
                  <div>
                    <Checkbox
                      id={group.id}
                      testId={group.testId}
                      className="checkbox-item"
                      checked={checkedDocs.includes(group.id)}
                      name={t(group.text || '')}
                      onChange={handleToggleCheckedDocs(group.id)}
                    />
                  </div>
                  <Box direction="column">
                    <Box onClick={handleToggleCheckedDocs(group.id)}>
                      <Text weight={400}>{t(group.text || '')}</Text>
                    </Box>
                    <Text>
                      {group.docs.map((doc) => {
                        return getWrapper(
                          <LinkButton
                            key={doc.title}
                            size="M"
                            linkInlineMode
                            onClick={() => handleClick(doc.src)}
                          >
                            {doc.title}
                          </LinkButton>,
                          doc,
                          group.docs
                        );
                      })}
                    </Text>
                  </Box>
                </Box>
              ))}
            </Box>
          </ErrorBoundary>
          <div>
            <Box direction="row" justify="end" align="center" margin={{ top: 'spacing12' }}>
              <AppButton
                buttonType="secondary"
                width={BUTTON_WIDTH}
                onClick={() => onCancel()}
                containerStyle={{ marginRight: theme.spacings.s }}
                testId="cancel-investments"
              >
                {t('Cancel')}
              </AppButton>

              <AppButton
                type="submit"
                testId="next-investments"
                onClick={handleNext}
                width={BUTTON_WIDTH}
                disabled={
                  citizenshipRejected ||
                  (isSubmitted && !!currentError) ||
                  isDocsLoading ||
                  isPathSaveLoading ||
                  isAllowedCitizenship === false
                }
                data-testid="submit_button"
              >
                {isPathSaveLoading ? (
                  <Preloader color="white" />
                ) : (
                  t(`Next (${step}/${STEPS_COUNT[selectedPath] || stepLength})`)
                )}
              </AppButton>
            </Box>
          </div>
        </Box>
        <Box background="border2" height="1px" width="100%" margin={{ top: 'l', bottom: 'm' }} />
        <Text size="large" weight="bold" margin={{ bottom: 'spacing12' }}>
          {t('Investment Fees')}
        </Text>
        <Text
          // margin={{ bottom: 'spacing24' }}
          size="large"
          color="textSecondary"
        >
          {t(
            'Self directed investments have no additional investment fees. Managed funds are .04% of your investment balance, billed quarterly and deducted automatically from your investment account.'
          )}
        </Text>
        {/* <Text size="large" color="textSecondary">
          {t(
            'In the event the cash portion of your account does not have a sufficient balance to pay your investment service fee, a negative balance will be reflected on your account until a contribution is posted to your account by your employer or a liquidation of your investment positions is initiated by you, the account holder.',
          )}
        </Text> */}
        <Box background="border2" height="1px" width="100%" margin={{ top: 'spacing24', bottom: 'm' }} />
        <Text size="large" color="textSecondary" margin={{ bottom: 'spacing24' }}>
          {t(
            'Investment advisory services offered through Intellicents investment solutions, inc., an SEC registered investment adviser.'
          )}
        </Text>
      </Box>
      {/* <Prompt
        when={selectedPath !== '' || isAcceptedDocs}
        message="You have unsaved changes, are you sure you want to leave?"
      /> */}
      <ServerErrorModal
        visible={errorWarningVisible}
        onSetVisible={setErrorWarningVisible}
        onSendRequest={handleSave}
        isLoading={isPathSaveLoading}
      />
    </Box>
  );
};

export default InvestmentPath;
